import { useEffect, useRef, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { ArrowUp, X } from "lucide-react";

const SESSION_KEY = "gpaplus_view_hint_shown";
const IDLE_MS = 5000;

// First-run coach-mark: once per session, after 5s of inactivity, point the
// user at the account menu so they can switch between role views.
export function ViewSwitchHint() {
  const { user } = useAuth();
  const [rect, setRect] = useState(null);
  const timerRef = useRef(null);

  const locate = useCallback(() => {
    const el = document.querySelector('[data-testid="user-menu-trigger"]');
    return el ? el.getBoundingClientRect() : null;
  }, []);

  const dismiss = useCallback(() => setRect(null), []);

  useEffect(() => {
    if (!user) return;
    if (sessionStorage.getItem(SESSION_KEY)) return;

    let cancelled = false;
    const schedule = () => {
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(show, IDLE_MS);
    };
    function show() {
      if (cancelled || sessionStorage.getItem(SESSION_KEY)) return;
      const r = locate();
      if (!r) { schedule(); return; } // menu not mounted yet — retry
      sessionStorage.setItem(SESSION_KEY, "1");
      setRect(r);
    }
    const reset = () => { if (!sessionStorage.getItem(SESSION_KEY)) schedule(); };

    const events = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "wheel"];
    events.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    schedule();
    return () => {
      cancelled = true;
      clearTimeout(timerRef.current);
      events.forEach((e) => window.removeEventListener(e, reset));
    };
  }, [user, locate]);

  // reposition + auto-dismiss while visible
  useEffect(() => {
    if (!rect) return;
    const reposition = () => { const r = locate(); if (r) setRect(r); };
    const el = document.querySelector('[data-testid="user-menu-trigger"]');
    window.addEventListener("resize", reposition);
    window.addEventListener("scroll", reposition, { passive: true });
    el?.addEventListener("click", dismiss);
    const auto = setTimeout(dismiss, 13000);
    return () => {
      window.removeEventListener("resize", reposition);
      window.removeEventListener("scroll", reposition);
      el?.removeEventListener("click", dismiss);
      clearTimeout(auto);
    };
  }, [rect, locate, dismiss]);

  if (!rect) return null;

  const top = rect.bottom + 14;
  const right = Math.max(12, window.innerWidth - rect.right);

  return (
    <div className="pointer-events-none fixed inset-0 z-[60]" data-testid="view-switch-hint">
      <span
        className="absolute animate-ping rounded-full ring-4 ring-[#78be21]"
        style={{ left: rect.left - 4, top: rect.top - 4, width: rect.width + 8, height: rect.height + 8 }}
      />
      <div className="pointer-events-auto absolute" style={{ top, right, maxWidth: 300 }}>
        <div className="relative rounded-2xl border border-[#78be21]/50 bg-[#003057] p-4 text-white shadow-2xl">
          <ArrowUp className="absolute -top-4 right-6 h-6 w-6 animate-bounce text-[#78be21]" />
          <div className="flex items-start justify-between gap-3">
            <p className="text-sm font-extrabold text-[#a8e063]">Try the different views</p>
            <button onClick={dismiss} data-testid="hint-dismiss" className="text-slate-300 hover:text-white"><X className="h-4 w-4" /></button>
          </div>
          <p className="mt-1 text-sm leading-relaxed text-slate-200">
            Open this menu to switch between the <strong>Student</strong>, <strong>Advisor</strong>,
            <strong> Administrator</strong>, and <strong>Employer</strong> experiences.
          </p>
          <ul className="mt-2 space-y-1 text-xs text-slate-300">
            <li>• Each view loads that role's real dashboard</li>
            <li>• Switch back anytime — nothing is saved</li>
            <li>• "My Dashboard" takes you home</li>
          </ul>
          <button onClick={dismiss} data-testid="hint-got-it" className="mt-3 w-full rounded-full bg-[#78be21] py-1.5 text-xs font-bold text-[#00243f] hover:bg-[#6aac1c]">
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}
